import React, { useEffect, useRef, useState } from "react";
import { loaders, posts } from "virtual:blog-index";
import { mountCharts } from "./charts";
import "./blog.css";

export { posts as blogPosts };
export type BlogPost = (typeof posts)[number];

type LinkComponent = (props: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) => React.ReactElement;

const SITE_URL = "https://stylome.io";
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Post bodies load as separate chunks. The prerender and the client entry
// both call loadBlogPost before the first render, so hydration sees the same
// HTML that the server wrote.
const bodies = new Map<string, string>();

export function blogSlugForRoute(route: string) {
  const slug = /^\/blog\/([a-z0-9-]+)$/.exec(route)?.[1];
  return slug && slug in loaders ? slug : undefined;
}

export async function loadBlogPost(slug: string) {
  if (bodies.has(slug)) return bodies.get(slug)!;
  const { default: post } = await loaders[slug]();
  bodies.set(slug, post.html);
  return post.html;
}

export function blogPostUrl(slug: string) {
  return `${SITE_URL}/blog/${slug}`;
}

export function formatPostDate(date: string) {
  const [year, month, day] = date.split("-").map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

function PostMetadata({ post }: { post: BlogPost }) {
  const parts = [
    formatPostDate(post.date),
    post.updated && `updated ${formatPostDate(post.updated)}`,
    post.author,
    `${post.readingMinutes} min read`,
  ].filter(Boolean);
  return (
    <p className="metadata-line">
      {post.draft && (
        <strong className="blog-draft">Draft, not published. </strong>
      )}
      {parts.join(" · ")}
    </p>
  );
}

export function BlogIndex({ Link }: { Link: LinkComponent }) {
  return (
    <div className="page blog-index">
      <header className="page-title">
        <h1>Blog</h1>
        <p className="lede">
          Measurements of AI-written text in courts, markets, forums, and other
          places where it matters who wrote the words.
        </p>
      </header>
      {posts.length ? (
        <ol className="blog-list">
          {posts.map((post) => (
            <li key={post.slug}>
              <h2>
                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
              </h2>
              <p>{post.description}</p>
              <PostMetadata post={post} />
            </li>
          ))}
        </ol>
      ) : (
        <p className="blog-empty">There&apos;s nothing here yet.</p>
      )}
      {posts.length > 0 && (
        <p className="metadata-line">
          <a href="/blog/feed.xml">RSS feed</a>
        </p>
      )}
    </div>
  );
}

function ShareLinks({ post }: { post: BlogPost }) {
  const url = blogPostUrl(post.slug);
  const [canShare, setCanShare] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setCanShare(typeof navigator.share === "function");
  }, []);
  const e = encodeURIComponent;
  const targets: Array<[string, string]> = [
    [
      "Bluesky",
      `https://bsky.app/intent/compose?text=${e(`${post.title} ${url}`)}`,
    ],
    ["X", `https://x.com/intent/tweet?url=${e(url)}&text=${e(post.title)}`],
    [
      "Reddit",
      `https://www.reddit.com/submit?url=${e(url)}&title=${e(post.title)}`,
    ],
    [
      "Hacker News",
      `https://news.ycombinator.com/submitlink?u=${e(url)}&t=${e(post.title)}`,
    ],
    [
      "LinkedIn",
      `https://www.linkedin.com/sharing/share-offsite/?url=${e(url)}`,
    ],
    [
      "Email",
      `mailto:?subject=${e(post.title)}&body=${e(`${post.description}\n\n${url}`)}`,
    ],
  ];

  async function copy() {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  }

  return (
    <section className="blog-share" aria-labelledby="share-heading">
      <h2 id="share-heading">Share this post</h2>
      <ul>
        {targets.map(([label, href]) => (
          <li key={label}>
            <a href={href} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          </li>
        ))}
        <li>
          <button type="button" className="text-button" onClick={copy}>
            {copied ? "Link copied" : "Copy link"}
          </button>
        </li>
        {canShare && (
          <li>
            <button
              type="button"
              className="text-button"
              onClick={() =>
                navigator
                  .share({ title: post.title, text: post.description, url })
                  .catch(() => {})
              }
            >
              More options
            </button>
          </li>
        )}
      </ul>
      <span className="sr-only" role="status">
        {copied ? "Link copied to clipboard" : ""}
      </span>
    </section>
  );
}

function Discussion({ post }: { post: BlogPost }) {
  const threads: Array<[string, string | undefined]> = [
    ["Hacker News", post.discussion.hacker_news],
    ["Reddit", post.discussion.reddit],
    ["Bluesky", post.discussion.bluesky],
  ];
  const present = threads.filter((thread): thread is [string, string] =>
    Boolean(thread[1]),
  );
  if (!present.length) return null;
  return (
    <p className="blog-discussion">
      Discuss this post on{" "}
      {present.map(([label, href], index) => (
        <React.Fragment key={label}>
          {index > 0 && (index === present.length - 1 ? " or " : ", ")}
          <a href={href}>{label}</a>
        </React.Fragment>
      ))}
      .
    </p>
  );
}

export function BlogPostPage({
  slug,
  Link,
}: {
  slug: string;
  Link: LinkComponent;
}) {
  const post = posts.find((candidate) => candidate.slug === slug)!;
  const [html, setHtml] = useState(() => bodies.get(slug));
  const body = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    if (!html) {
      void loadBlogPost(slug).then((loaded) => {
        if (active) setHtml(loaded);
      });
    }
    return () => {
      active = false;
    };
  }, [slug, html]);

  useEffect(() => {
    if (!html || !body.current) return;
    return mountCharts(body.current);
  }, [html]);

  return (
    <article className="page blog-post">
      <header className="blog-post-header">
        <h1>{post.title}</h1>
        <p className="lede">{post.description}</p>
        <PostMetadata post={post} />
      </header>
      {html ? (
        <div
          className="blog-body"
          ref={body}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <div className="blog-body" role="status">
          Loading the post.
        </div>
      )}
      <div className="blog-post-footer">
        <ShareLinks post={post} />
        <Discussion post={post} />
        <p>
          <Link to="/blog">All posts</Link>
        </p>
      </div>
    </article>
  );
}
